import { Participant } from 'api/Serializers/Clients';
import Button from 'components/button';
import ButtonLarge from 'components/button-large';
import Callout from 'components/callout';
import Controls from 'components/controls';
import SelectParticipants from 'containers/participant-select';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { OneParticipantIcon, TwoParticipantIcon } from 'icons';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getAccountParticipants,
  getCartAppointmentProduct,
  getCartNumParticipants,
  getCartParticipants,
  getUsername,
} from 'state/selectors';
import { setCartParticipants, setNumParticipants } from 'state/slice/cart';
import { BookingHeader } from '../components';
import { StepProps } from '../types';

const EditCartParticipants = ({
  onClickContinue,
}: Pick<StepProps<string>, 'onClickContinue'>) => {
  const maxParticipants = 2; // one day this may be set by the activity data
  const dispatch = useAppDispatch();
  const username = useSelector(getUsername);
  const selectedParticipants = useSelector(getCartParticipants);
  const accountParticipants = useSelector(getAccountParticipants);
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const numParticipants = useSelector(getCartNumParticipants);
  const participantNoun =
    appointmentProduct.activity.clientDescription.toLowerCase();
  const [showError, setShowError] = useState(false);
  const [showAuthenticationModal, setShowAuthModal] = useState(false);

  const handleNumParticipants = (num: number) => {
    if (num > maxParticipants || num < 1) {
      return;
    }
    dispatch(setNumParticipants(num));
    setShowError(false);
  };

  const handleContinue = () => {
    const isError = numParticipants !== selectedParticipants.length;
    setShowError(isError);
    if (!isError) {
      onClickContinue(selectedParticipants);
    }
  };

  const handleParticipantSelect = (
    position: number,
    participant: Participant
  ) => {
    const participants = Array(numParticipants)
      .fill(0)
      .map((_, i) => (i === position ? participant : selectedParticipants[i]));
    dispatch(setCartParticipants(participants));
  };

  useEffect(() => {
    if (
      accountParticipants !== undefined &&
      numParticipants > selectedParticipants.length
    ) {
      const participants = [];
      for (var i = 0; i < numParticipants; i++) {
        if (selectedParticipants[i] !== undefined) {
          participants.push(selectedParticipants[i]);
        } else {
          const candidate = accountParticipants.find(
            (p) =>
              selectedParticipants.findIndex((s) => s.id === p.id) === -1 &&
              participants.findIndex((s) => s.id === p.id) === -1
          );
          participants.push(candidate);
        }
      }
      dispatch(setCartParticipants(participants));
    }
  }, [numParticipants]);

  return (
    <>
      <div className="mb-4 space-y-3">
        <h3 className="font-semibold text-md">Select {participantNoun}s</h3>
        <p>
          How many {participantNoun}s will be in the{' '}
          {appointmentProduct.activity.appointmentNoun.toLowerCase()}?
        </p>
        <div className="hidden gap-2 md:flex ">
          <ButtonLarge
            icon={<OneParticipantIcon width={24} />}
            title={`1 ${participantNoun.capitalize()}`}
            onClick={() => handleNumParticipants(1)}
            selected={numParticipants === 1}
          />
          <ButtonLarge
            icon={<TwoParticipantIcon width={24} />}
            title={`2 ${participantNoun.capitalize()}s`}
            onClick={() => handleNumParticipants(2)}
            selected={numParticipants === 2}
          />
        </div>
        <div className="flex gap-2 md:hidden">
          <ButtonLarge
            icon={<OneParticipantIcon width={24} />}
            title={`1 ${participantNoun.capitalize()}`}
            subtitle={`${appointmentProduct.price.toCurrency()}/hr`}
            onClick={() => handleNumParticipants(1)}
            selected={numParticipants === 1}
          />
          <ButtonLarge
            icon={<TwoParticipantIcon width={24} />}
            title={`2 ${participantNoun.capitalize()}s`}
            subtitle={`${(
              appointmentProduct.price +
              appointmentProduct.extraParticipantPrice
            ).toCurrency()}/hr`}
            onClick={() => handleNumParticipants(2)}
            selected={numParticipants === 2}
          />
        </div>
        {!!username && (
          <>
            {numParticipants !== undefined && (
              <SelectParticipants
                username={username}
                disabled={typeof numParticipants === 'undefined'}
                errorIfEmpty={showError}
                participantNoun={participantNoun}
                numParticipants={numParticipants}
                selectedParticipants={selectedParticipants}
                onSelect={(position: number, participant: Participant) =>
                  handleParticipantSelect(position, participant)
                }
              />
            )}
            {showError && (
              <Callout
                type="error"
                title={`Enter all ${participantNoun}s to continue`}
              >
                Help your{' '}
                {appointmentProduct.activity.instructorDescription.toLowerCase()}{' '}
                prepare by letting them know who is coming!
              </Callout>
            )}
            <Controls>
              <Button
                color="primary"
                variant="contained"
                onClick={handleContinue}
              >
                Continue
              </Button>
            </Controls>
          </>
        )}
      </div>
    </>
  );
};
const SelectCartParticipants = ({
  state,
  onClickContinue,
  onClickEdit,
  bookingType,
}: StepProps<string>) => {
  if (state === 'hidden') {
    return null;
  }
  const selectedParticipants = useSelector(getCartParticipants);
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const participantNoun =
    appointmentProduct.activity.clientDescription.toLowerCase();

  return (
    <div>
      {state === 'complete' ? (
        <div>
          <BookingHeader
            onEdit={onClickEdit}
            title={`${selectedParticipants.length} ${participantNoun
              .capitalize()
              .pluralize(selectedParticipants.length)} `}
          />
          <div className="space-y-4">
            {selectedParticipants.map((participant) => (
              <div key={participant.id}>
                <div className="labeled-icon">
                  <OneParticipantIcon width={18} />
                  <span className="font-semibold">{participant.name}</span>
                </div>
                <div className="ml-6 pl-1.5">
                  <p className="!leading-normal text-sm mb-0 whitespace-pre-wrap">
                    {participant.notes !== ''
                      ? participant.notes
                      : 'No notes given'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <EditCartParticipants onClickContinue={onClickContinue} />
      )}
    </div>
  );
};

export default SelectCartParticipants;
